import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControlLabel,
  CircularProgress,
  Chip,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  TuneRounded,
  RestartAltRounded,
  ExpandMoreRounded,
  InfoOutlined,
} from "@mui/icons-material";
import colors from "../../Constants/colors";
import theme from "../../Constants/theme";
import { useTranslation } from "react-i18next";
import { getConfigDefaults } from "../../Api/evaluations";
import { EvaluationConfigDefaults } from "../../Models/evaluation";
import { ComoSwitch } from "../Common/ComoSwitch";

type ConfigValue = number | boolean | string;

interface ConfigOverrideStepProps {
  configOverrides: Record<string, ConfigValue>;
  onConfigChange: (overrides: Record<string, ConfigValue>) => void;
  allowedFields?: Set<keyof EvaluationConfigDefaults>;
}

interface FieldGroup {
  titleKey: string;
  fields: (keyof EvaluationConfigDefaults)[];
}

interface FieldMeta {
  min?: number;
  max?: number;
  step?: number;
}

const FIELD_META: Partial<Record<keyof EvaluationConfigDefaults, FieldMeta>> = {
  grading_temperature: { min: 0, max: 2, step: 0.05 },
  improvement_temperature: { min: 0, max: 2, step: 0.05 },
  summarization_temperature: { min: 0, max: 2, step: 0.05 },
  default_retrieval_result_limit: { min: 1, max: 100, step: 1 },
  default_dense_company_data_retrieval_limit: { min: 1, max: 100, step: 1 },
  default_dense_certification_knowledge_retrieval_limit: {
    min: 1,
    max: 100,
    step: 1,
  },
  default_smart_search_limit: { min: 1, max: 100, step: 1 },
  default_improvement_note_limit: { min: 1, max: 100, step: 1 },
  target_ingestion_chunk_size: { min: 100, max: 10000, step: 100 },
  improvement_note_query_chunk_size_tokens: { min: 50, max: 5000, step: 50 },
  improvement_note_query_chunk_overlap_tokens: { min: 0, max: 2000, step: 10 },
  improvement_note_query_chunk_max_chunks: { min: 1, max: 50, step: 1 },
  improvement_note_query_chunk_per_query_limit: { min: 1, max: 50, step: 1 },
  company_data_similarity_threshold: { min: 0, max: 1, step: 0.01 },
  company_kb_chunks_ratio: { min: 0, max: 10, step: 0.05 },
  context_window_based_token_limit: { min: 1000, max: 200000, step: 1000 },
  reranker_multiplier: { min: 1, max: 10, step: 0.5 },
  hybrid_search_alpha: { min: 0, max: 1, step: 0.05 },
};

const STRATEGY_OPTIONS = [
  { value: "plain", labelKey: "evaluationPage.strategyOptions.plain" },
  { value: "enriched", labelKey: "evaluationPage.strategyOptions.enriched" },
  {
    value: "rag_optimized",
    labelKey: "evaluationPage.strategyOptions.rag_optimized",
  },
  {
    value: "enriched_rag_optimized",
    labelKey: "evaluationPage.strategyOptions.enriched_rag_optimized",
  },
  { value: "keywords", labelKey: "evaluationPage.strategyOptions.keywords" },
  {
    value: "enriched_keywords",
    labelKey: "evaluationPage.strategyOptions.enriched_keywords",
  },
];

const SELECT_FIELDS = new Set<keyof EvaluationConfigDefaults>([
  "question_embedding_strategy" as keyof EvaluationConfigDefaults,
]);

const FIELD_GROUPS: FieldGroup[] = [
  {
    titleKey: "evaluationPage.configGroups.temperatureSettings",
    fields: [
      "grading_temperature",
      "improvement_temperature",
      "summarization_temperature",
    ],
  },
  {
    titleKey: "evaluationPage.configGroups.retrievalLimits",
    fields: [
      "default_retrieval_result_limit",
      "default_dense_company_data_retrieval_limit",
      "default_dense_certification_knowledge_retrieval_limit",
      "default_smart_search_limit",
      "default_improvement_note_limit",
    ],
  },
  {
    titleKey: "evaluationPage.configGroups.chunkingSettings",
    fields: [
      "target_ingestion_chunk_size",
      "improvement_note_query_chunk_size_tokens",
      "improvement_note_query_chunk_overlap_tokens",
      "improvement_note_query_chunk_max_chunks",
      "improvement_note_query_chunk_per_query_limit",
    ],
  },
  {
    titleKey: "evaluationPage.configGroups.similarityAndRatios",
    fields: [
      "company_data_similarity_threshold",
      "company_kb_chunks_ratio",
      "context_window_based_token_limit",
      "reranker_multiplier",
    ],
  },
  {
    titleKey: "evaluationPage.configGroups.embeddingStrategy",
    fields: ["question_embedding_strategy"],
  },
  {
    titleKey: "evaluationPage.configGroups.featureToggles",
    fields: ["reranker_enabled", "improvement_note_late_chunking_enabled"],
  },
  {
    titleKey: "evaluationPage.configGroups.hybridSearch",
    fields: ["hybrid_search_enabled", "hybrid_search_alpha"],
  },
];

const ConfigOverrideStep: React.FC<ConfigOverrideStepProps> = ({
  configOverrides,
  onConfigChange,
  allowedFields,
}) => {
  const { t } = useTranslation("evaluationPage");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [defaults, setDefaults] = useState<EvaluationConfigDefaults | null>(
    null,
  );
  const [currentValues, setCurrentValues] =
    useState<EvaluationConfigDefaults | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchDefaults = async () => {
      try {
        setIsLoading(true);
        const data = await getConfigDefaults();
        if (!cancelled) {
          setDefaults(data);

          // Apply any previously set overrides on top of defaults
          if (Object.keys(configOverrides).length > 0) {
            setCurrentValues({
              ...data,
              ...configOverrides,
            } as EvaluationConfigDefaults);
          } else {
            setCurrentValues(data);
          }
        }
      } catch {
        if (!cancelled) {
          setError(t("evaluationPage.configOverrideError"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchDefaults();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleValueChange = (
    field: keyof EvaluationConfigDefaults,
    value: ConfigValue,
  ) => {
    if (!defaults || !currentValues) return;

    const updatedValues = { ...currentValues, [field]: value };
    setCurrentValues(updatedValues as EvaluationConfigDefaults);

    // Compute diff: only include fields that differ from defaults
    const overrides: Record<string, ConfigValue> = {};
    for (const key of Object.keys(
      defaults,
    ) as (keyof EvaluationConfigDefaults)[]) {
      if (updatedValues[key] !== defaults[key]) {
        overrides[key] = updatedValues[key];
      }
    }
    onConfigChange(overrides);
  };

  const handleResetField = (field: keyof EvaluationConfigDefaults) => {
    if (!defaults) return;
    handleValueChange(field, defaults[field]);
  };

  const isFieldChanged = (field: keyof EvaluationConfigDefaults): boolean => {
    if (!defaults || !currentValues) return false;
    return currentValues[field] !== defaults[field];
  };

  const changedCount = defaults
    ? (Object.keys(defaults) as (keyof EvaluationConfigDefaults)[]).filter(
        isFieldChanged,
      ).length
    : 0;

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress sx={{ color: colors.primary_dark }} />
        </Box>
      </Box>
    );
  }

  if (error || !defaults || !currentValues) {
    return (
      <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${colors.red200}`,
            backgroundColor: colors.red50,
          }}
        >
          <Typography color="error">
            {error || t("evaluationPage.configOverrideError")}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${colors.primary_dark}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TuneRounded sx={{ color: colors.primary_dark, fontSize: 28 }} />
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 600,
                color: colors.primary_dark,
                marginBottom: 0,
              }}
            >
              {t("evaluationPage.configOverride")}
            </Typography>
          </Box>
          {changedCount > 0 && (
            <Chip
              label={t("evaluationPage.configOverrideChangedCount", {
                count: changedCount,
              })}
              size="small"
              sx={{
                backgroundColor: colors.orange50,
                color: colors.orange600,
                fontWeight: 600,
                border: `1px solid ${colors.orange200}`,
              }}
            />
          )}
        </Box>
        <Typography variant="body2" sx={{ color: colors.gray500, mt: 1 }}>
          {t("evaluationPage.configOverrideDescription")}
        </Typography>
      </Paper>

      {/* Field Groups */}
      {(allowedFields
        ? FIELD_GROUPS.map((g) => ({
            ...g,
            fields: g.fields.filter((f) => allowedFields.has(f)),
          })).filter((g) => g.fields.length > 0)
        : FIELD_GROUPS
      ).map((group) => (
        <Accordion
          key={group.titleKey}
          defaultExpanded={false}
          elevation={0}
          disableGutters
          sx={{
            mb: 2,
            borderRadius: "8px !important",
            border: `1px solid ${colors.gray200}`,
            "&:before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreRounded sx={{ color: colors.primary_dark }} />
            }
            sx={{
              px: 2.5,
              "& .MuiAccordionSummary-content": {
                my: 1.5,
              },
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: colors.primary_dark,
              }}
            >
              {t(group.titleKey)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr",
                },
                gap: 2,
              }}
            >
              {group.fields.map((field) => {
                const value = currentValues[field];
                const changed = isFieldChanged(field);

                if (SELECT_FIELDS.has(field)) {
                  return (
                    <Box
                      key={field}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: changed
                          ? colors.orange50
                          : colors.gray50,
                        border: `1px solid ${
                          changed ? colors.orange200 : colors.gray200
                        }`,
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: changed ? 600 : 400,
                              color: colors.gray700,
                              fontSize: "0.8rem",
                            }}
                          >
                            {t(`evaluationPage.configFields.${field}`)}
                          </Typography>
                          <Tooltip
                            title={t(`evaluationPage.configTooltips.${field}`)}
                            arrow
                            placement="top"
                          >
                            <InfoOutlined
                              sx={{
                                fontSize: 14,
                                color: colors.gray400,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        </Box>
                        {changed && (
                          <RestartAltRounded
                            sx={{
                              fontSize: 18,
                              color: colors.gray400,
                              cursor: "pointer",
                              "&:hover": { color: colors.accent },
                            }}
                            onClick={() => handleResetField(field)}
                          />
                        )}
                      </Box>
                      <FormControl fullWidth size="small">
                        <Select
                          value={value as string}
                          onChange={(e) =>
                            handleValueChange(field, e.target.value as string)
                          }
                          sx={{
                            backgroundColor: colors.white,
                            color: colors.gray900,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: changed
                                ? colors.orange200
                                : colors.gray300,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.accent,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.accent,
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: `${colors.white} !important`,
                                color: `${colors.gray900} !important`,
                              },
                            },
                          }}
                        >
                          {STRATEGY_OPTIONS.map((opt) => (
                            <MenuItem
                              key={opt.value}
                              value={opt.value}
                              sx={{
                                color: `${colors.gray900} !important`,
                                backgroundColor: `${colors.white} !important`,
                                "&.Mui-selected": {
                                  backgroundColor: `${colors.gray100} !important`,
                                  color: `${colors.gray900} !important`,
                                },
                                "&.Mui-selected:hover": {
                                  backgroundColor: `${colors.gray200} !important`,
                                },
                                "&:hover": {
                                  backgroundColor: `${colors.gray100} !important`,
                                },
                              }}
                            >
                              {t(opt.labelKey)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                }

                if (typeof value === "boolean") {
                  return (
                    <Box
                      key={field}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: changed
                          ? colors.orange50
                          : colors.gray50,
                        border: `1px solid ${
                          changed ? colors.orange200 : colors.gray200
                        }`,
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <ComoSwitch
                            checked={value}
                            onChange={(e) =>
                              handleValueChange(field, e.target.checked)
                            }
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: changed ? 600 : 400,
                                color: colors.gray700,
                                fontSize: "0.8rem",
                              }}
                            >
                              {t(`evaluationPage.configFields.${field}`)}
                            </Typography>
                            <Tooltip
                              title={t(
                                `evaluationPage.configTooltips.${field}`,
                              )}
                              arrow
                              placement="top"
                            >
                              <InfoOutlined
                                sx={{
                                  fontSize: 14,
                                  color: colors.gray400,
                                  cursor: "help",
                                }}
                              />
                            </Tooltip>
                          </Box>
                        }
                      />
                      {changed && (
                        <RestartAltRounded
                          sx={{
                            fontSize: 18,
                            color: colors.gray400,
                            cursor: "pointer",
                            "&:hover": { color: colors.accent },
                          }}
                          onClick={() => handleResetField(field)}
                        />
                      )}
                    </Box>
                  );
                }

                return (
                  <Box
                    key={field}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: changed
                        ? colors.orange50
                        : colors.gray50,
                      border: `1px solid ${
                        changed ? colors.orange200 : colors.gray200
                      }`,
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: changed ? 600 : 400,
                            color: colors.gray700,
                            fontSize: "0.8rem",
                          }}
                        >
                          {t(`evaluationPage.configFields.${field}`)}
                        </Typography>
                        <Tooltip
                          title={t(`evaluationPage.configTooltips.${field}`)}
                          arrow
                          placement="top"
                        >
                          <InfoOutlined
                            sx={{
                              fontSize: 14,
                              color: colors.gray400,
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      </Box>
                      {changed && (
                        <RestartAltRounded
                          sx={{
                            fontSize: 18,
                            color: colors.gray400,
                            cursor: "pointer",
                            "&:hover": { color: colors.accent },
                          }}
                          onClick={() => handleResetField(field)}
                        />
                      )}
                    </Box>
                    <TextField
                      type="number"
                      value={value}
                      onChange={(e) => {
                        const parsed = parseFloat(e.target.value);
                        if (!isNaN(parsed)) {
                          handleValueChange(field, parsed);
                        }
                      }}
                      size="small"
                      fullWidth
                      inputProps={{
                        step:
                          FIELD_META[field]?.step ??
                          (typeof defaults[field] === "number" &&
                          !Number.isInteger(defaults[field])
                            ? 0.01
                            : 1),
                        min: FIELD_META[field]?.min,
                        max: FIELD_META[field]?.max,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: colors.white,
                          "& fieldset": {
                            borderColor: changed
                              ? colors.orange200
                              : colors.gray300,
                          },
                          "&:hover fieldset": {
                            borderColor: colors.accent,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.accent,
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          color: colors.gray900,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ConfigOverrideStep;
